"use client";

import { ErrorBoundary as BricksErrorBoundary } from "@red-digital/bricks";
import { type ErrorInfo } from "react";
import { isAssetLoadError, recover } from "./AssetLoadErrorHandler";

/**
 * Drop-in for the bricks `ErrorBoundary`. Render-time asset-load failures
 * (a failed `React.lazy` / `next/dynamic` chunk) drive {@link recover} and log
 * a clean New Relic event; every other error falls through to bricks unchanged.
 */
export class ErrorBoundary extends BricksErrorBoundary {
  private assetRetriesExhausted = false;

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isAssetLoadError(error)) {
      this.assetRetriesExhausted = recover(error, "error-boundary");
      // Flip to error state to stop the catch loop while reloading; skip the
      // bricks logError since recover() already logged.
      this.setState({ hasError: true, error, errorInfo });
      return;
    }
    super.componentDidCatch(error, errorInfo);
  }

  render() {
    if (this.state.hasError && isAssetLoadError(this.state.error) && !this.assetRetriesExhausted) {
      // Reload in flight — render nothing instead of flashing the bricks
      // fallback (shown once retries are exhausted).
      return null;
    }
    return super.render();
  }
}
